import type { Logger } from '@feature-hub/core';
import fetch from '@strictsoftware/fetch';
import type {
	BffError,
	BffLoggerCall,
	BffPath,
	BffResponse,
	BffUrl,
	CommonQueryParams,
	HandlerParams,
	HandlerPayload,
	RequestTiming,
	ServiceMetadata
} from '@visualizer/bff';

export type RequestMeta = {
	readonly name: string;
	readonly url: string;
	readonly createdAt: string;
	readonly fetchedAt: string;
	readonly dataVersion: string;
	readonly requestTimings: RequestTiming[];
	readonly serviceMetadata: ServiceMetadata[];
};

export type RegisterRequestMetaCB = (requestMeta: RequestMeta) => void;

export interface BffFetchOptions extends CommonQueryParams {
	readonly baseUrl?: string;
	readonly logger: Logger;
	readonly registerRequestMeta?: RegisterRequestMetaCB;
}

export class BffFetchError extends Error {
	public constructor(
		message: string,
		public readonly bffLoggerCalls?: BffLoggerCall[]
	) {
		super(message);
	}
}

type LambdaError = { readonly errorType: string };

/** Fetch data from one of the available BFF routes */
export async function fetchBffData<P extends BffPath>(
	pathname: P,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	{ baseUrl = '', logger, registerRequestMeta, ...params }: BffFetchOptions & HandlerParams<P>
): Promise<BffResponse<HandlerPayload<P>> & { url: BffUrl<P> }> {
	const searchParams = new URLSearchParams();

	for (const [key, value] of Object.entries(params).sort()) {
		if (typeof value === 'string') {
			searchParams.set(key, value);
		} else if (value === true) {
			searchParams.set(key, 'true');
		} else if (Array.isArray(value)) {
			searchParams.set(key, value.join('-'));
		} else if (typeof value === 'object') {
			searchParams.set(key, JSON.stringify(value));
		} else if (typeof value === 'number') {
			searchParams.set(key, value.toString());
		}
	}

	const url: BffUrl<P> = `${baseUrl}${baseUrl.endsWith('/') ? 'bff' : '/bff'}${pathname}?${searchParams.toString()}`;

	const response = await fetch(url);

	const bffData = (await response.json()) as BffResponse<HandlerPayload<P>> | BffError | LambdaError;

	if ('errorType' in bffData) {
		throw new BffFetchError(`Fatal Error: ${bffData.errorType}`);
	}

	if ('message' in bffData) {
		throw new BffFetchError(bffData.message, bffData.bffLoggerCalls);
	}

	registerRequestMeta?.({
		url,
		name: pathname,
		dataVersion: bffData.dataVersion,
		createdAt: bffData.createdAt,
		fetchedAt: new Date().toISOString(),
		requestTimings: bffData.requestTimings,
		serviceMetadata: bffData.serviceMetadata
	});

	return { url, ...bffData };
}
